/*
 * Focus policy for a UI that is driven by a TV remote, a mouse AND a finger.
 *
 * Two rules, both of which exist because this app is not TV-only:
 *
 * 1. NOTHING is focused on page load. A mouse or touch viewer must never see a
 *    focus ring on an element they did not choose. Focus is claimed lazily, by
 *    the first arrow key — that press REVEALS focus rather than moving it.
 * 2. The ring is only painted for keyboard/remote input. This is `:focus-visible`
 *    semantics, but implemented as an attribute on <html> so it also works on the
 *    older Chromium builds that Smart TVs ship (Tizen/webOS are years behind, and
 *    `:focus-visible` / `:has()` cannot be relied on there).
 *
 * Real DOM focus is the source of truth for what is highlighted (see Focusable),
 * so Tab and the remote can never disagree. This module only decides WHEN focus
 * first appears and WHETHER it is drawn.
 *
 * Module-scoped by design: the styleguide is bundled once and shared by the shell
 * and every pilet, so these listeners are installed exactly once for the whole app.
 */
import { SpatialNavigation } from '@noriginmedia/norigin-spatial-navigation'

const ARROW_KEYS = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']

/** Controls where left/right mean "move the caret", not "move the focus". */
const TEXT_ENTRY = 'input:not([type=button]):not([type=submit]):not([type=reset]):not([type=checkbox]):not([type=radio]), textarea, [contenteditable=""], [contenteditable="true"]'

function isTypingInText (): boolean {
  const el = document.activeElement
  return el instanceof HTMLElement && el.matches(TEXT_ENTRY)
}

/** Attribute written on <html>; CSS gates the focus ring on it. */
const MODALITY_ATTR = 'data-input-modality'

export type InputModality = 'key' | 'pointer'

export function getInputModality (): InputModality {
  if (typeof document === 'undefined') return 'key'
  return document.documentElement.getAttribute(MODALITY_ATTR) === 'pointer' ? 'pointer' : 'key'
}

/** True when the viewer is driving with a remote/keyboard rather than a pointer. */
export function isKeyboardModality (): boolean {
  return typeof document !== 'undefined' && document.documentElement.hasAttribute(MODALITY_ATTR) && getInputModality() === 'key'
}

function setModality (modality: InputModality) {
  document.documentElement.setAttribute(MODALITY_ATTR, modality)
}

/** Whether the spatial tree currently points at an element that still exists. */
function hasLiveFocus (): boolean {
  const current = SpatialNavigation.getCurrentFocusKey()
  return Boolean(current) && SpatialNavigation.doesFocusableExist(current)
}

function onKeyDown (event: KeyboardEvent) {
  // Any key means the viewer is on a remote/keyboard — start drawing rings.
  setModality('key')

  // Text fields: left/right belong to the caret, not to spatial navigation.
  // Norigin's own listener calls preventDefault() on every key in its keymap
  // regardless of what is focused, which otherwise freezes the caret and drags
  // focus out of the field mid-word. Stopping propagation here keeps Norigin from
  // ever seeing the key; the browser still performs the default action (moving the
  // caret), because propagation and default actions are independent.
  // Up/Down are deliberately left alone — on a TV they are the only way back out
  // of the field.
  if (isTypingInText() && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
    event.stopImmediatePropagation()
    return
  }

  if (!ARROW_KEYS.includes(event.key)) return
  if (hasLiveFocus()) return

  // Nothing focused yet: this press claims the page's first stop (DOM order) and
  // does nothing else.
  const entry = document.querySelector('[data-focus-key]')
  const focusKey = entry?.getAttribute('data-focus-key')
  if (!focusKey) return

  event.preventDefault()
  // Swallow the press so Norigin's own key handler does not ALSO act on it: this
  // listener runs in the capture phase, so by the time Norigin sees the event the
  // tree would have a live focus and it would navigate away from the stop we just
  // claimed (pressing Down first would land two stops in, not on the first).
  event.stopPropagation()
  event.stopImmediatePropagation()
  SpatialNavigation.setFocus(focusKey)
}

if (typeof window !== 'undefined') {
  window.addEventListener('keydown', onKeyDown, { capture: true })
  window.addEventListener('pointerdown', () => setModality('pointer'), { capture: true })
}
